import { clerkApiClient as apiClient } from './clerk-api-client';
import { 
  createEntityBitemporal, 
  updateEntityBitemporal, 
  deactivateEntity, 
  reactivateEntity
} from './bitemporalApi';
import type {
  Credit,
  User,
  Instructor,
  Location,
  Room,
  ClassCategory,
  Class,
  CreditPackage,
  Membership,
  Booking,
  StoreItem
} from './api';

// Interfaces for types not yet defined
interface ClassTemplate {
  id: string;
  name: string;
  description?: string;
  location_id: string;
  room_id: string;
  instructor_id: string;
  class_category_id: string;
  day_of_week: number;
  start_time: string;
  duration_minutes: number;
  difficulty_level?: string;
  is_active: boolean;
  created_at: string;
}

interface PromoCode {
  id: string;
  code: string;
  description?: string;
  discount_percentage?: number;
  discount_amount?: number;
  valid_from: string;
  valid_until: string;
  max_uses?: number;
  current_uses: number;
  applicable_types: string[];
  applicable_ids: string[];
  is_active: boolean;
  created_at: string;
}

interface Admin {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  is_active: boolean;
  created_at: string;
}

interface AuthUser {
  user: User;
  clerk_uid: string;
  is_admin: boolean;
}

interface UserWallet {
  user_id: string;
  balance: number;
  pending_balance: number;
  last_transaction_at?: string;
  credits: Array<{
    credit_id: string;
    credit_name: string;
    quantity: number;
    expires_at?: string;
  }>;
  memberships: Array<{
    membership_id: string;
    membership_name: string;
    expires_at: string;
    usage_this_period: Record<string, number>;
  }>;
}

interface DashboardMetrics {
  total_users: number;
  active_users_30d: number;
  total_revenue_30d: number;
  total_classes_30d: number;
  total_bookings_30d: number;
  average_class_attendance: number;
  popular_classes: Array<{
    class_name: string;
    booking_count: number;
  }>;
  revenue_by_day: Array<{
    date: string;
    revenue: number;
  }>;
}

interface CartValidation {
  promo_code: string;
  items: Array<{
    type: string;
    id: string;
    quantity: number;
  }>;
}

interface CartCalculation {
  items: Array<{
    type: string;
    id: string;
    quantity: number;
    price: number;
  }>;
  promo_code?: string;
}

// API Service Class with backward compatibility
export class JuntoApiService {
  // Utility method to get the base URL
  static getBaseUrl(): string {
    return apiClient.getBaseUrl();
  }

  // Credits
  static async getCredits(includeInactive = false): Promise<Credit[]> {
    return apiClient.get<Credit[]>('/credits', { include_inactive: includeInactive });
  }

  static async createCredit(credit: Omit<Credit, 'id' | 'created_at'>, createdBy?: string): Promise<Credit> {
    return createEntityBitemporal('credits', credit, createdBy) as Promise<Credit>;
  }

  static async updateCredit(credit: Credit, updatedBy?: string, reason?: string): Promise<Credit> {
    return updateEntityBitemporal('credits', credit.id, credit, updatedBy, reason) as Promise<Credit>;
  }

  static async deleteCredit(id: string): Promise<void> {
    await this.deactivateCredit(id);
  }

  static async deactivateCredit(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('credits', id, deactivatedBy);
  }

  static async activateCredit(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('credits', id, reactivatedBy);
  }

  // Credit Packages
  static async getCreditPackages(includeInactive = false): Promise<CreditPackage[]> {
    return apiClient.get<CreditPackage[]>('/credit-packages', { include_inactive: includeInactive });
  }

  static async createCreditPackage(
    creditPackage: Omit<CreditPackage, 'id' | 'created_at' | 'updated_at'>,
    createdBy?: string
  ): Promise<CreditPackage> {
    return createEntityBitemporal('credit-packages', creditPackage, createdBy) as Promise<CreditPackage>;
  }

  static async updateCreditPackage(creditPackage: CreditPackage, updatedBy?: string, _reason?: string): Promise<CreditPackage> {
    // Use direct PUT endpoint instead of modify endpoint to avoid sending full object
    const client = await import('./clerk-api-client').then(m => m.clerkApiClient);
    const url = `/bitemporal/credit-packages/${creditPackage.id}${updatedBy ? `?updated_by=${encodeURIComponent(updatedBy)}` : ''}`;
    const response = await client.put<CreditPackage>(url, creditPackage);
    return response;
  }

  static async deleteCreditPackage(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('credit-packages', id, deactivatedBy);
  }

  // Users
  static async getUsers(includeInactive = false): Promise<User[]> {
    // Workaround: Add query param directly to avoid server redirect
    const response = await apiClient.get<{ users: User[]; total_count: number; page: number; page_size: number }>(`/users/?include_inactive=${includeInactive}`);
    return response.users || [];
  }

  static async getUser(id: string): Promise<User> {
    return apiClient.get<User>(`/users/${id}`);
  }

  static async createUser(user: Omit<User, 'id' | 'created_at'>, createdBy?: string): Promise<User> {
    return createEntityBitemporal('users', user, createdBy) as Promise<User>;
  }

  static async updateUser(user: User, updatedBy?: string, reason?: string): Promise<User> {
    const { id, created_at: _created_at, ...updates } = user;
    return updateEntityBitemporal('users', id, updates, updatedBy, reason) as Promise<User>;
  }

  static async deleteUser(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('users', id, deactivatedBy);
  }

  static async deactivateUser(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('users', id, deactivatedBy);
  }

  static async activateUser(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('users', id, reactivatedBy);
  }
  
  static async syncAllClerkUsers(): Promise<{ synced_count: number; skipped_count: number; total_processed: number }> {
    return apiClient.post<{ synced_count: number; skipped_count: number; total_processed: number }>('/users/sync-all-clerk-users');
  }

  // User Wallet
  static async getUserWallet(userId: string): Promise<UserWallet> {
    return apiClient.get<UserWallet>(`/users/${userId}/wallet`);
  }

  // Instructors
  static async getInstructors(includeInactive = false): Promise<Instructor[]> {
    return apiClient.get<Instructor[]>('/instructors', { include_inactive: includeInactive });
  }

  static async getInstructor(id: string): Promise<Instructor> {
    return apiClient.get<Instructor>(`/instructors/${id}`);
  }

  static async createInstructor(instructor: Omit<Instructor, 'id' | 'created_at'>, createdBy?: string): Promise<Instructor> {
    return createEntityBitemporal('instructors', instructor, createdBy) as Promise<Instructor>;
  }

  static async updateInstructor(instructor: Instructor, updatedBy?: string, reason?: string): Promise<Instructor> {
    const { id, created_at: _created_at, ...updates } = instructor;
    return updateEntityBitemporal('instructors', id, updates, updatedBy, reason) as Promise<Instructor>;
  }

  static async deleteInstructor(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('instructors', id, deactivatedBy);
  }

  static async deactivateInstructor(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('instructors', id, deactivatedBy);
  }

  static async activateInstructor(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('instructors', id, reactivatedBy);
  }

  // Locations
  static async getLocations(includeInactive = false): Promise<Location[]> {
    return apiClient.get<Location[]>('/locations', { include_inactive: includeInactive });
  }

  static async getLocation(id: string): Promise<Location> {
    return apiClient.get<Location>(`/locations/${id}`);
  }

  static async createLocation(location: Omit<Location, 'id' | 'created_at'>, createdBy?: string): Promise<Location> {
    return createEntityBitemporal('locations', location, createdBy) as Promise<Location>;
  }

  static async updateLocation(location: Location, updatedBy?: string, reason?: string): Promise<Location> {
    const { id, created_at: _created_at, ...updates } = location;
    return updateEntityBitemporal('locations', id, updates, updatedBy, reason) as Promise<Location>;
  }

  static async deleteLocation(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('locations', id, deactivatedBy);
  }

  static async deactivateLocation(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('locations', id, deactivatedBy);
  }

  static async activateLocation(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('locations', id, reactivatedBy);
  }

  // Rooms
  static async getRooms(includeInactive = false): Promise<Room[]> {
    return apiClient.get<Room[]>('/rooms', { include_inactive: includeInactive });
  }

  static async getRoom(id: string): Promise<Room> {
    return apiClient.get<Room>(`/rooms/${id}`);
  }

  static async createRoom(room: Omit<Room, 'id' | 'created_at'>, createdBy?: string): Promise<Room> {
    return createEntityBitemporal('rooms', room, createdBy) as Promise<Room>;
  }

  static async updateRoom(room: Room, updatedBy?: string, reason?: string): Promise<Room> {
    const { id, created_at: _created_at, ...updates } = room;
    return updateEntityBitemporal('rooms', id, updates, updatedBy, reason) as Promise<Room>;
  }

  static async deleteRoom(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('rooms', id, deactivatedBy);
  }

  static async deactivateRoom(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('rooms', id, deactivatedBy);
  }

  static async activateRoom(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('rooms', id, reactivatedBy);
  }

  // Class Categories
  static async getClassCategories(includeInactive = false): Promise<ClassCategory[]> {
    return apiClient.get<ClassCategory[]>('/class-categories', { include_inactive: includeInactive });
  }

  static async getClassCategory(id: string): Promise<ClassCategory> {
    return apiClient.get<ClassCategory>(`/class-categories/${id}`);
  }

  static async createClassCategory(category: Omit<ClassCategory, 'id' | 'created_at'>, createdBy?: string): Promise<ClassCategory> {
    return createEntityBitemporal('class-categories', category, createdBy) as Promise<ClassCategory>;
  }

  static async updateClassCategory(category: ClassCategory, updatedBy?: string, reason?: string): Promise<ClassCategory> {
    const { id, created_at: _created_at, ...updates } = category;
    return updateEntityBitemporal('class-categories', id, updates, updatedBy, reason) as Promise<ClassCategory>;
  }

  static async deleteClassCategory(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('class-categories', id, deactivatedBy);
  }

  static async deactivateClassCategory(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('class-categories', id, deactivatedBy);
  }

  static async activateClassCategory(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('class-categories', id, reactivatedBy);
  }

  // Classes
  static async getClasses(includeInactive = false): Promise<Class[]> {
    return apiClient.get<Class[]>('/classes', { include_inactive: includeInactive });
  }

  static async getClass(id: string): Promise<Class> {
    return apiClient.get<Class>(`/classes/${id}`);
  }

  static async createClass(classData: Omit<Class, 'id' | 'created_at'>, createdBy?: string): Promise<Class> {
    return createEntityBitemporal('classes', classData, createdBy) as Promise<Class>;
  }

  static async updateClass(classData: Class, updatedBy?: string, reason?: string): Promise<Class> {
    const { id, created_at: _created_at, ...updates } = classData;
    return updateEntityBitemporal('classes', id, updates, updatedBy, reason) as Promise<Class>;
  }

  static async deleteClass(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('classes', id, deactivatedBy);
  }

  static async deactivateClass(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('classes', id, deactivatedBy);
  }

  static async activateClass(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('classes', id, reactivatedBy);
  }

  static async cancelClass(id: string): Promise<Class> {
    return apiClient.put<Class>(`/classes/${id}/cancel`);
  }

  // Memberships
  static async getMemberships(includeInactive = false): Promise<Membership[]> {
    return apiClient.get<Membership[]>('/memberships', { include_inactive: includeInactive });
  }

  static async getMembership(id: string): Promise<Membership> {
    return apiClient.get<Membership>(`/memberships/${id}`);
  }

  static async createMembership(membership: Omit<Membership, 'id' | 'created_at'>, createdBy?: string): Promise<Membership> {
    return createEntityBitemporal('memberships', membership, createdBy) as Promise<Membership>;
  }

  static async updateMembership(membership: Membership, updatedBy?: string, reason?: string): Promise<Membership> {
    const { id, created_at: _created_at, ...updates } = membership;
    return updateEntityBitemporal('memberships', id, updates, updatedBy, reason) as Promise<Membership>;
  }

  static async deleteMembership(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('memberships', id, deactivatedBy);
  }

  static async deactivateMembership(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('memberships', id, deactivatedBy);
  }

  static async activateMembership(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('memberships', id, reactivatedBy);
  }

  // Bookings
  static async getBookings(includeInactive = false): Promise<Booking[]> {
    try {
      return apiClient.get<Booking[]>('/bookings', { include_inactive: includeInactive });
    } catch (error: any) {
      // Backend issue: 'JuntoController' object has no attribute 'get_all_bookings_admin'
      // This is a backend implementation issue where the documented GET /api/v2/bookings endpoint
      // is not properly implemented. The error suggests the backend is routing to an incorrect method.
      if (error?.response?.status === 500 && error?.response?.data?.errors?.[0]?.message?.includes('get_all_bookings_admin')) {
        console.error('Backend Issue: The GET /api/v2/bookings endpoint is not properly implemented. The backend is trying to call "get_all_bookings_admin" which does not exist.');
        
        // Return empty array as fallback to prevent UI crash
        // TODO: Remove this fallback once backend implements GET /api/v2/bookings correctly
        return [];
      }
      throw error;
    }
  }

  static async getBooking(id: string): Promise<Booking> {
    return apiClient.get<Booking>(`/bookings/${id}`);
  }

  static async createBooking(booking: Omit<Booking, 'id' | 'created_at' | 'updated_at'>, createdBy?: string): Promise<Booking> {
    return createEntityBitemporal('bookings', booking, createdBy) as Promise<Booking>;
  }

  static async updateBooking(id: string, booking: Partial<Booking>, updatedBy?: string): Promise<Booking> {
    return updateEntityBitemporal('bookings', id, booking, updatedBy) as Promise<Booking>;
  }

  static async cancelBooking(id: string, deactivatedBy?: string): Promise<void> {
    await deactivateEntity('bookings', id, deactivatedBy);
  }

  static async deactivateBooking(id: string, deactivatedBy?: string): Promise<void> {
    await deactivateEntity('bookings', id, deactivatedBy);
  }

  static async activateBooking(id: string, reactivatedBy?: string): Promise<void> {
    await reactivateEntity('bookings', id, reactivatedBy);
  }

  // Store Items
  static async getStoreItems(includeInactive = false): Promise<StoreItem[]> {
    return apiClient.get<StoreItem[]>('/store-items', { include_inactive: includeInactive });
  }

  static async getStoreItem(id: string): Promise<StoreItem> {
    return apiClient.get<StoreItem>(`/store-items/${id}`);
  }

  static async createStoreItem(item: Omit<StoreItem, 'id' | 'created_at' | 'updated_at'>, createdBy?: string): Promise<StoreItem> {
    return createEntityBitemporal('store-items', item, createdBy) as Promise<StoreItem>;
  }

  static async updateStoreItem(item: StoreItem, updatedBy?: string, reason?: string): Promise<StoreItem> {
    const { id, created_at: _created_at, updated_at: _updated_at, ...updates } = item;
    return updateEntityBitemporal('store-items', id, updates, updatedBy, reason) as Promise<StoreItem>;
  }

  static async deleteStoreItem(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('store-items', id, deactivatedBy);
  }

  static async deactivateStoreItem(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('store-items', id, deactivatedBy);
  }

  static async activateStoreItem(id: string, reactivatedBy?: string, _reason?: string): Promise<void> {
    await reactivateEntity('store-items', id, reactivatedBy);
  }

  // Dashboard
  static async getDashboardMetrics(): Promise<DashboardMetrics> {
    return apiClient.get<DashboardMetrics>('/dashboard/metrics');
  }

  // Class Templates
  static async getClassTemplates(includeInactive = false): Promise<ClassTemplate[]> {
    return apiClient.get<ClassTemplate[]>('/class-templates', { include_inactive: includeInactive });
  }

  static async createClassTemplate(template: Omit<ClassTemplate, 'id' | 'created_at'>, createdBy?: string): Promise<ClassTemplate> {
    return createEntityBitemporal('class-templates', template, createdBy) as Promise<ClassTemplate>;
  }

  static async updateClassTemplate(template: ClassTemplate, updatedBy?: string, reason?: string): Promise<ClassTemplate> {
    const { id, created_at: _created_at, ...updates } = template;
    return updateEntityBitemporal('class-templates', id, updates, updatedBy, reason) as Promise<ClassTemplate>;
  }

  static async deleteClassTemplate(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('class-templates', id, deactivatedBy);
  }

  static async generateClassesFromTemplate(templateId: string, weeksAhead?: number): Promise<Class[]> {
    return apiClient.post<Class[]>(`/class-templates/${templateId}/generate`, {
      weeks_ahead: weeksAhead
    });
  }

  // Promo Codes
  static async getPromoCodes(includeInactive = false): Promise<PromoCode[]> {
    return apiClient.get<PromoCode[]>('/promo-codes', { include_inactive: includeInactive });
  }

  static async getPromoCode(id: string): Promise<PromoCode> {
    return apiClient.get<PromoCode>(`/promo-codes/${id}`);
  }

  static async createPromoCode(promoCode: Omit<PromoCode, 'id' | 'created_at' | 'current_uses'>, createdBy?: string): Promise<PromoCode> {
    // Set default current_uses for new promo codes
    const promoCodeWithDefaults = { ...promoCode, current_uses: 0 };
    return createEntityBitemporal('promo-codes', promoCodeWithDefaults, createdBy) as Promise<PromoCode>;
  }

  static async updatePromoCode(promoCode: PromoCode, updatedBy?: string, reason?: string): Promise<PromoCode> {
    const { id, created_at: _created_at, current_uses: _current_uses, ...updates } = promoCode;
    return updateEntityBitemporal('promo-codes', id, updates, updatedBy, reason) as Promise<PromoCode>;
  }

  static async deactivatePromoCode(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('promo-codes', id, deactivatedBy);
  }

  static async deletePromoCode(id: string, deactivatedBy?: string, _reason?: string): Promise<void> {
    await deactivateEntity('promo-codes', id, deactivatedBy);
  }

  // Cart Operations
  static async validateCartWithPromoCode(validation: CartValidation): Promise<{
    is_valid: boolean;
    discount_amount: number;
    errors: string[];
  }> {
    return apiClient.post('/cart/validate', validation);
  }

  static async calculateCartTotal(calculation: CartCalculation): Promise<{
    subtotal: number;
    discount: number;
    total: number;
  }> {
    return apiClient.post('/cart/calculate', calculation);
  }

  // Health Check
  static async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return apiClient.get('/health');
  }

  // Admin Management
  static async getAdmins(includeInactive = false): Promise<Admin[]> {
    return apiClient.get<Admin[]>('/admins', { include_inactive: includeInactive });
  }

  static async getAdmin(id: string): Promise<Admin> {
    return apiClient.get<Admin>(`/admins/${id}`);
  }

  static async createAdmin(admin: { user_id: string }): Promise<Admin> {
    return apiClient.post<Admin>('/admins', admin);
  }

  static async updateAdmin(id: string, admin: Partial<Admin>): Promise<Admin> {
    return apiClient.put<Admin>(`/admins/${id}`, admin);
  }

  static async deleteAdmin(id: string): Promise<void> {
    await apiClient.delete(`/admins/${id}`);
  }

  // Auth Operations
  static async getCurrentUser(): Promise<AuthUser> {
    return apiClient.get<AuthUser>('/auth/me');
  }

  static async syncClerkUser(): Promise<AuthUser> {
    return apiClient.post<AuthUser>('/auth/sync-clerk-user');
  }
}

export default JuntoApiService;